import type {
  DelFiltra,
  EnotaKazalnika,
  KazalnikPregleda,
  NajvecjiStroski,
  PregledKazalnikov as Pregled,
  SignalPregleda,
} from '@koda/shared';
import { useState, type JSX } from 'react';

import { decimalno, evri, znesek, znesekZValuto } from '../lib/format';
import { useBesedila, type Besedila } from '../lib/jezik';
import { useTelefon } from '../lib/telefon';
import type { Celica } from './PanelRazclenitve';
import { celicaDelov, Lupa, obdobjeLeta, sPredznakom, vsotaDelov } from './kazalniki-skupno';
import { GibanjeIzracun } from './GibanjeIzracun';
import { GrafPrihodkov } from './GrafPrihodkov';
import { GibanjeOkence, KarticaKazalnika, SeznamKazalnikov, VezavaKartice } from './KarticaKazalnika';
import { PrimerjavaDoMeseca } from './PrimerjavaDoMeseca';
import { UstvarjanjeVrednosti } from './UstvarjanjeVrednosti';
import { ZaPregled } from './ZaPregled';

interface Lastnosti {
  pregled: Pregled;
  naCelico: (celica: Celica) => void;
  naPodrobno: () => void;
}


/**
 * Vrednost kazalnika v njegovi enoti, s slovenskim zapisom. Kazalnik s
 * formulo ima lahko svoje stevilo decimalk (`decimalke`).
 */
function vEnoti(vrednost: string, enota: EnotaKazalnika, b: Besedila, decimalke?: number): string {
  switch (enota) {
    case 'odstotek':
      return `${decimalno(vrednost, decimalke ?? 1)} %`;
    case 'krat':
      return `${decimalno(vrednost, decimalke ?? 2)} ${b.pk.krat}`;
    case 'meseci':
      return `${decimalno(vrednost, decimalke ?? 2)} ${b.pk.meseci}`;
    case 'eur':
      // Brez decimalk s strežnika (starejši odgovor) na evre, sicer tako,
      // kot je vrednost zaokrožena na strežniku.
      return decimalke === undefined ? evri(vrednost) : `${decimalno(vrednost, decimalke)} €`;
  }
}

/** Razlika z izrecnim predznakom; pri odstotkih v odstotnih tockah. */
function razlikaVEnoti(razlika: string, enota: EnotaKazalnika, b: Besedila, decimalke?: number): string {
  const predznak = Number(razlika) > 0 ? '+' : '';
  if (enota === 'odstotek') return `${predznak}${decimalno(razlika, decimalke ?? 1)} ${b.pk.odstotneTocke}`;
  return `${predznak}${vEnoti(razlika, enota, b, decimalke)}`;
}


/** Puscica smeri; barva nikoli ne nosi pomena sama, zato je ob stevilki tudi znak. */
function smer(razlika: string): string {
  const z = Number(razlika);
  return z > 0 ? '▲' : z < 0 ? '▼' : '=';
}

/**
 * Stran Pregled modula Kazalniki (docs/ARHITEKTURA.md, 6.3), po zasnovi
 * »Kazalniki – dashboard« (od vrha): Za pregled (~2/3) in Prihodki po mesecih
 * (~1/3); skupine kazalnikov brez Gibanja v eni vrsti čez celo širino;
 * obratni kapital s karticami in pod vsako njenim okencem Gibanje; Ustvarjanje
 * vrednosti (~2/3) in Največja stroška (~1/3); trak Primerjava do meseca n;
 * opomba in gumb »Vsi kazalniki in tabele«. Ploščice so nad stranjo (glava,
 * skupna s Podrobno). Na telefonu en stolpec v vrstnem redu zasnove, kazalniki
 * kot seznam. Vsaka številka, ki je vsota knjižb, odpre razčlenitev.
 */
/** Največ kartic kazalnikov v eni vrsti (zasnova). */
const KARTIC_V_VRSTI = 4;
/** Ključ odprtega izračuna gibanja (okence »Gibanje«) – ločen od kartice. */
const kljucGibanja = (koda: string): string => `gibanje:${koda}`;

export function PregledKazalnikov({ pregled, naCelico, naPodrobno }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const telefon = useTelefon();
  const [odprt, setOdprt] = useState<string | null>(null);
  const preklopi = (koda: string): void => {
    setOdprt(odprt === koda ? null : koda);
  };

  const skupine = pregled.kazalniki.reduce<{ skupina: string; naziv: string; kazalniki: KazalnikPregleda[] }[]>(
    (vse, k) => {
      const obstojeca = vse.find((s) => s.skupina === k.skupina);
      if (obstojeca) obstojeca.kazalniki.push(k);
      else vse.push({ skupina: k.skupina, naziv: k.nazivSkupine, kazalniki: [k] });
      return vse;
    },
    [],
  );
  // V vrsti so največ štiri kartice: skupina z več kazalniki (npr. Po meri)
  // se razdeli na dele po štiri, naziv ima samo prvi del. Zaporedni deli brez
  // Gibanja so v isti vrsti, dokler je kartic največ štiri; skupina z
  // Gibanjem ima svojo vrsto, da je okence pod svojo kartico.
  const deli = skupine.flatMap((s) =>
    Array.from({ length: Math.ceil(s.kazalniki.length / KARTIC_V_VRSTI) }, (_, i) => ({
      ...s,
      kljuc: `${s.skupina}#${String(i)}`,
      nadaljevanje: i > 0,
      kazalniki: s.kazalniki.slice(i * KARTIC_V_VRSTI, (i + 1) * KARTIC_V_VRSTI),
    })),
  );
  const vrste: (typeof deli)[] = [];
  for (const s of deli) {
    const zGibanjem = s.kazalniki.some((k) => k.gibanje !== undefined);
    const zadnja = vrste[vrste.length - 1];
    const karticVZadnji = zadnja?.reduce((v, x) => v + x.kazalniki.length, 0) ?? 0;
    if (!zGibanjem && zadnja !== undefined && !zadnja.some((x) => x.kazalniki.some((k) => k.gibanje !== undefined)) && karticVZadnji + s.kazalniki.length <= KARTIC_V_VRSTI) {
      zadnja.push(s);
    } else {
      vrste.push([s]);
    }
  }

  const prihodki = pregled.ploscice.find((p) => p.koda === 'prihodki');
  const uv = pregled.ustvarjanjeVrednosti ?? null;
  const ns = pregled.najvecjiStroski ?? null;
  const primerjalno = pregled.imaPrimerjalno ? pregled.primerjalno : null;
  const obdobje = obdobjeLeta(pregled.n, pregled.leto, b.meseci);

  const izracun = (k: KazalnikPregleda, zGibanjem = true): JSX.Element => (
    <IzracunKazalnika
      key={`izracun-${k.koda}`}
      kazalnik={k}
      zGibanjem={zGibanjem}
      signali={pregled.signali.filter((x) => x.kazalnik === k.koda)}
      leto={pregled.leto}
      primerjalno={primerjalno}
      naCelico={naCelico}
      naZapri={() => {
        setOdprt(null);
      }}
    />
  );

  // Pregled ima vsebino, kadar je kateri koli razdelek kazalnikov: skalarni
  // kazalniki, Ustvarjanje vrednosti, Največja stroška ali signal (npr. COST_01).
  const imaKazalnike = pregled.kazalniki.length > 0 || uv !== null || ns !== null || pregled.signali.length > 0;
  const zaPregled =
    imaKazalnike ? (
      <ZaPregled
        signali={pregled.signali}
        ocenjeni={pregled.signaliOcenjeni}
        opomba={pregled.opombaSignalov}
        naIzracun={(koda) => {
          setOdprt(koda);
          // Kartica je lahko daleč spodaj; brskalnik jo pokaže.
          document.getElementById(`kazalnik-${koda}`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        }}
      />
    ) : null;
  const graf =
    prihodki !== undefined && prihodki.poMesecih.t.length > 0 ? (
      <GrafPrihodkov ploscica={prihodki} leto={pregled.leto} primerjalno={primerjalno} naCelico={naCelico} />
    ) : null;
  const vrednost =
    uv === null ? null : (
      <UstvarjanjeVrednosti
        razdelek={uv}
        obdobje={obdobjeLeta(uv.n, uv.leto, b.meseci)}
        odprt={odprt === uv.koda}
        naOdpri={() => {
          preklopi(uv.koda);
        }}
        naCelico={naCelico}
      />
    );
  const stroski =
    ns === null ? null : (
      <NajvecjaStroska
        stroski={ns}
        imaPrimerjalno={pregled.imaPrimerjalno}
        odprt={odprt === ns.koda}
        naOdpri={() => {
          preklopi(ns.koda);
        }}
        naCelico={naCelico}
      />
    );
  const trak =
    pregled.primerjava !== undefined && pregled.primerjava.length > 0 ? (
      <PrimerjavaDoMeseca
        stolpci={pregled.primerjava}
        leto={pregled.leto}
        primerjalno={primerjalno}
        n={pregled.n}
        obdobje={obdobje}
        naCelico={naCelico}
      />
    ) : null;
  const brez = !imaKazalnike ? <p className="namig">{b.pk.brezKazalnikov}</p> : null;

  if (telefon) {
    const odprtKazalnik = pregled.kazalniki.find((k) => k.koda === odprt);
    return (
      <div className="pregled-kazalnikov kz-telefon">
        {zaPregled}
        {brez}
        {pregled.kazalniki.length > 0 && (
          <section className="kz-karta" aria-label={b.pk.kazalniki}>
            <div className="kz-karta-naslov">
              <h2>{b.pk.kazalniki}</h2>
            </div>
            <SeznamKazalnikov kazalniki={pregled.kazalniki} signali={pregled.signali} odprt={odprt} naOdpri={preklopi} />
            {odprtKazalnik !== undefined && izracun(odprtKazalnik)}
          </section>
        )}
        {vrednost}
        {stroski}
        {graf}
        {trak}
        <button type="button" className="kz-gumb-glavni" onClick={naPodrobno}>
          {b.pk.vsiKazalniki}
        </button>
        <p className="kz-noga-opomba">{b.pk.nogaOpomba}</p>
      </div>
    );
  }

  return (
    <div className="pregled-kazalnikov">
      {(zaPregled !== null || graf !== null) && (
        <div className={`kz-vrsta-2-1${zaPregled !== null && graf !== null ? ' oba' : ''}`}>
          {zaPregled}
          {graf}
        </div>
      )}
      {brez}

      {vrste.map((vrsta) => {
        const zGibanjem = vrsta.some((s) => s.kazalniki.some((k) => k.gibanje !== undefined));
        const vseKartice = vrsta.flatMap((s) => s.kazalniki);
        const odprtVVrsti = vseKartice.find((k) => k.koda === odprt);
        // Izračun gibanja odpre sivo okence, ne kartica nad njim.
        const gibanjeVVrsti = vseKartice.find((k) => k.gibanje !== undefined && kljucGibanja(k.koda) === odprt);
        return (
          <div key={vrsta.map((s) => s.kljuc).join('|')} className="kz-vrsta-skupin">
            <div className="kz-skupine" style={{ gridTemplateColumns: vrsta.map((s) => `${String(s.kazalniki.length)}fr`).join(' ') }}>
              {vrsta.map((s) => (
                <section key={s.kljuc} className="kz-skupina" aria-label={s.naziv}>
                  {/* Nadaljevanje skupine v novi vrsti: prazen naziv obdrži poravnavo kartic. */}
                  <h3 className="kz-skupina-naziv" aria-hidden={s.nadaljevanje ? true : undefined}>
                    {s.nadaljevanje ? '\u00a0' : s.naziv}
                  </h3>
                  <div
                    className="kz-kartice"
                    style={{
                      gridTemplateColumns: `repeat(${String(zGibanjem || vrsta.length > 1 ? s.kazalniki.length : KARTIC_V_VRSTI)}, minmax(0, 1fr))`,
                    }}
                  >
                    {s.kazalniki.map((k) => (
                      // Kartica in pod njo okence »Gibanje« sta en stolpec mreže,
                      // da so okenca poravnana s karticami v isti vrsti.
                      <div key={k.koda} className="kz-stolpec-kazalnika">
                        <KarticaKazalnika
                          kazalnik={k}
                          signali={pregled.signali.filter((x) => x.kazalnik === k.koda)}
                          odprt={odprt === k.koda}
                          naOdpri={() => {
                            preklopi(k.koda);
                          }}
                        />
                        {k.gibanje !== undefined && (
                          <GibanjeOkence
                            naziv={k.naziv}
                            gibanje={k.gibanje}
                            odprt={odprt === kljucGibanja(k.koda)}
                            naOdpri={() => {
                              preklopi(kljucGibanja(k.koda));
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
            {odprtVVrsti !== undefined && izracun(odprtVVrsti, false)}
            {gibanjeVVrsti?.gibanje !== undefined && (
              <section className="izracun-kazalnika" aria-labelledby={`naslov-gibanja-${gibanjeVVrsti.koda}`}>
                <header>
                  <h3 id={`naslov-gibanja-${gibanjeVVrsti.koda}`}>
                    {gibanjeVVrsti.gibanje.kratko?.naslov ?? `${b.pk.gibanje}: ${gibanjeVVrsti.naziv}`}
                  </h3>
                  <button
                    type="button"
                    className="povezava-gumb"
                    onClick={() => {
                      setOdprt(null);
                    }}
                  >
                    {b.pk.skrijIzracun}
                  </button>
                </header>
                <GibanjeIzracun gibanje={gibanjeVVrsti.gibanje} naCelico={naCelico} brezNaslova />
              </section>
            )}
          </div>
        );
      })}

      {(vrednost !== null || stroski !== null) && (
        // Ustvarjanje vrednosti (~2/3) in Največja stroška (~1/3) v isti vrsti;
        // razdelek, ki ga ni, ne pusti praznega mesta.
        <div className={`vrednost-in-stroski${vrednost !== null && stroski !== null ? ' oba' : ''}`}>
          {vrednost}
          {stroski}
        </div>
      )}

      {trak}

      <div className="kz-noga">
        <p className="kz-noga-opomba">{b.pk.nogaOpomba}</p>
        <button type="button" className="kz-gumb-glavni" onClick={naPodrobno}>
          {b.pk.vsiKazalniki} <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  );
}







interface LastnostiIzracuna {
  kazalnik: KazalnikPregleda;
  signali: SignalPregleda[];
  leto: number;
  primerjalno: number | null;
  naCelico: (celica: Celica) => void;
  /** Brez tega (predogled v graditelju) gumba za zapiranje ni. */
  naZapri?: () => void;
  /**
   * Ali je v izračunu tudi gibanje stanja. Na spletu ga odpre okence
   * »Gibanje« samo (`false`); na telefonu okenca ni, zato je tu.
   */
  zGibanjem?: boolean;
}

/**
 * »Poglej izracun«: letos, lani, razlika, opisna razlaga (formula, kaj pove
 * in cesa ne), vrednosti po mesecih (zlozene) in sestavine do knjizb.
 * Razclenitev sestavin bere filtre iz odgovora - poizvedba se sprozi sele ob
 * kliku na znesek.
 */
export function IzracunKazalnika({ kazalnik: k, signali, leto, primerjalno, naCelico, naZapri, zGibanjem = true }: LastnostiIzracuna): JSX.Element {
  const b = useBesedila();
  const sestavina = (naziv: string, vrednost: string | null, obdobje: string, deli: DelFiltra[]): JSX.Element => {
    if (vrednost === null) return <td className="stevilka">{b.pk.ni}</td>;
    if (deli.length === 0) return <td className="stevilka">{znesek(vrednost)}</td>;
    return (
      <td className="stevilka">
        <button
          type="button"
          className="znesek"
          title={b.namigCelice}
          onClick={() => {
            naCelico(celicaDelov(naziv, obdobje, vrednost, deli));
          }}
        >
          {znesek(vrednost)}
        </button>
      </td>
    );
  };
  return (
    <section className="izracun-kazalnika" aria-label={k.naziv}>
      <header>
        <h3>{k.naziv}</h3>
        {naZapri !== undefined && (
          <button type="button" className="povezava-gumb" onClick={naZapri}>
            {b.pk.skrijIzracun}
          </button>
        )}
      </header>
      {signali.map((s) => (
        <div key={s.pravilo} className="signal v-izracunu">
          <Lupa />
          <p>
            <strong>{s.naslov}.</strong> {s.besedilo}
          </p>
        </div>
      ))}
      <table className="primerjava-kazalnika">
        <thead>
          <tr>
            <th>{`${b.pk.letos} (${String(leto)})`}</th>
            <th>{k.nazivLani ?? (primerjalno === null ? b.pk.lani : `${b.pk.lani} (${String(primerjalno)})`)}</th>
            <th>{b.pk.razlika}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{k.vrednost === null ? (k.razlog ?? b.pk.ni) : vEnoti(k.vrednost, k.enota, b, k.decimalke)}</td>
            <td>{k.lani === null ? (k.razlogLani ?? b.pk.ni) : vEnoti(k.lani, k.enota, b, k.decimalke)}</td>
            <td>
              {k.razlika === null ? (
                b.pk.ni
              ) : (
                <>
                  <span aria-hidden="true">{smer(k.razlika)}</span> {razlikaVEnoti(k.razlika, k.enota, b, k.decimalke)}
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>
      {k.oznaka !== null && <p className="drobno">{k.oznaka}</p>}
      {k.vezava !== undefined && (
        <>
          <VezavaKartice vezava={k.vezava} razred="kz-vezava v-izracunu" />
          <p className="drobno">{k.vezava.razlogDni ?? k.vezava.opisDni}</p>
        </>
      )}
      <div className="razlaga-kazalnika">
        <p>
          <strong>{b.pk.formula}:</strong> {k.formula}
        </p>
        {k.opis !== '' && (
          <p>
            <strong>{b.pk.kajPove}:</strong> {k.opis}
          </p>
        )}
      </div>

      <h4>{b.pk.sestavine}</h4>
      <table className="sestavine-izracuna">
        <tbody>{vrsticeSestavin(k, leto, primerjalno, sestavina)}</tbody>
      </table>

      {zGibanjem && k.gibanje !== undefined && <GibanjeIzracun gibanje={k.gibanje} naCelico={naCelico} />}

      <details className="po-mesecih">
        <summary>{b.pk.poMesecih}</summary>
        <PoMesecih kazalnik={k} leto={leto} primerjalno={primerjalno} />
      </details>
    </section>
  );
}

/**
 * Vrstice sestavin z naslovi stolpcev: bilancna vrstica nosi datum stanja
 * (»31. 7. 2026«), tokovna obdobje (»jan–jul 2026«). Kadar se oznaki od
 * prejsnje vrstice razlikujeta, pred vrstico pride nova vrstica naslovov;
 * brez oznake ostane leto.
 */
function vrsticeSestavin(
  k: KazalnikPregleda,
  leto: number,
  primerjalno: number | null,
  sestavina: (naziv: string, vrednost: string | null, obdobje: string, deli: DelFiltra[]) => JSX.Element,
): JSX.Element[] {
  const vrstice: JSX.Element[] = [];
  let prejsnji: string | null = null;
  k.sestavine.forEach((s, i) => {
    const oznakaT = s.obdobjeT ?? String(leto);
    // Brez oznake leto samo, kadar je ni nobena vrstica (starejši odgovor); če
    // ima letošnja vrstica oznako, lanska pa ne (lanske vrednosti ni, npr. leto
    // pred primerjalnim manjka), ostane naslov prazen – leto bi bilo napačno.
    const oznakaT1 = s.obdobjeT1 ?? (primerjalno === null || s.obdobjeT != null ? '' : String(primerjalno));
    const kljuc = `${oznakaT}|${oznakaT1}`;
    if (kljuc !== prejsnji) {
      vrstice.push(
        <tr key={`glava-${String(i)}`} className="glava-sestavin">
          <td />
          <th scope="col" className="stevilka">
            {oznakaT}
          </th>
          {primerjalno !== null && (
            <th scope="col" className="stevilka">
              {oznakaT1}
            </th>
          )}
        </tr>,
      );
      prejsnji = kljuc;
    }
    vrstice.push(
      <tr key={s.naziv}>
        <th scope="row" className="postavka">
          {s.naziv}
        </th>
        {sestavina(s.naziv, s.t, oznakaT, s.deliT)}
        {primerjalno !== null && sestavina(s.naziv, s.t1, oznakaT1, s.deliT1)}
      </tr>,
    );
  });
  return vrstice;
}

interface LastnostiPoMesecih {
  kazalnik: KazalnikPregleda;
  leto: number;
  primerjalno: number | null;
}

/**
 * Vrednost kazalnika po mesecih kot tabela (namesto grafa). Lanska vrednost je
 * lahko ena sama referenca (npr. 31. 12.) - takrat je napisana pod tabelo.
 * Razmerje ni vsota knjizb, zato vrednosti niso klikljive.
 */
function PoMesecih({ kazalnik: k, leto, primerjalno }: LastnostiPoMesecih): JSX.Element {
  const b = useBesedila();
  const { t, t1, referencaT1 } = k.poMesecih;
  const zLani = primerjalno !== null && t1.length > 0;
  const meseci = Math.max(t.length, zLani ? t1.length : 0);
  const vrednost = (v: string | null | undefined): string => (v == null ? b.pk.ni : vEnoti(v, k.enota, b, k.decimalke));
  if (meseci === 0) return <p className="drobno">{b.pk.ni}</p>;
  return (
    <>
      <table className="sestavine-izracuna">
        <thead>
          <tr>
            <th scope="col">{b.pk.mesec}</th>
            <th scope="col" className="stevilka">
              {leto}
            </th>
            {zLani && (
              <th scope="col" className="stevilka">
                {primerjalno}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: meseci }, (_, i) => (
            <tr key={i}>
              <th scope="row" className="postavka">
                {b.meseci[i] ?? String(i + 1)}
              </th>
              <td className="stevilka">{vrednost(t[i])}</td>
              {zLani && <td className="stevilka">{vrednost(t1[i])}</td>}
            </tr>
          ))}
        </tbody>
      </table>
      {referencaT1 != null && k.nazivLani !== undefined && (
        <p className="drobno">{`${k.nazivLani}: ${vrednost(referencaT1)}`}</p>
      )}
      {/* Potek je iste vrste kot vrednost: kadar je vrednost ocena, je ocena
          tudi potek - in to mora biti napisano ob njem. */}
      {k.oznaka !== null && <p className="drobno">{k.oznaka}</p>}
    </>
  );
}

/** Širina traku rasti: 3 px na odstotno točko, največ 75 px (±25 %) od ničle na sredini. */
const PX_NA_ODSTOTEK = 3;
const POL_TRAKU = 75;

/** Vodoraven trak rasti: od ničle na sredini desno (rast) ali levo (padec). */
function TrakRasti({ rast, barva }: { rast: string; barva: string }): JSX.Element {
  // Na desetinko piksla: 11,4 × 3 je 34,2, ne 34,199999….
  const w = Math.round(Math.min(Math.abs(Number(rast)) * PX_NA_ODSTOTEK, POL_TRAKU) * 10) / 10;
  const x = Number(rast) >= 0 ? POL_TRAKU : Math.round((POL_TRAKU - w) * 10) / 10;
  return (
    <svg width="150" height="10" viewBox="0 0 150 10" aria-hidden="true">
      <rect x="0" y="4" width="150" height="2" fill="#f0eee8" />
      <rect x={x} y="0" width={w} height="10" rx="2" fill={barva} />
      <rect x="74.2" y="0" width="1.5" height="10" fill="#8a877f" />
    </svg>
  );
}

interface LastnostiStroskov {
  stroski: NajvecjiStroski;
  imaPrimerjalno: boolean;
  odprt: boolean;
  naOdpri: () => void;
  naCelico: (celica: Celica) => void;
}


/**
 * Največja stroška (6.3): dve največji postavki stroškov, vsaka z deležem v
 * poslovnih prihodkih, rastjo stroška in rastjo prihodkov kot trakovoma in
 * oznako »Za pregled«, kadar strošek raste hitreje od prihodkov. Zneski
 * odprejo knjižbe (v knjižbah je strošek negativen).
 */
function NajvecjaStroska({ stroski: ns, imaPrimerjalno, odprt, naOdpri, naCelico }: LastnostiStroskov): JSX.Element {
  const b = useBesedila();
  const telefon = useTelefon();
  const [pomen, setPomen] = useState(false);
  const obdobje = obdobjeLeta(ns.n, ns.leto, b.meseci);
  const obdobjeLani = obdobjeLeta(ns.n, ns.primerjalno, b.meseci);
  // Razčlenitev pokaže znesek, kot je v knjižbah: vsoto delov (strošek je
  // negativen, poslovni prihodki pozitivni), ne prikazanega zneska.
  const odpri = (naziv: string, naslov: string, deli: DelFiltra[]): void => {
    naCelico(celicaDelov(naziv, naslov, vsotaDelov(deli), deli));
  };
  const rastIzpis = (rast: string | null): string => (rast === null ? b.pk.ni : `${sPredznakom(rast)} %`);
  /** Znesek s knjižbami je gumb, ki jih odpre; brez knjižb (npr. lani 0) je samo besedilo. */
  const znesekCelica = (naziv: string, naslov: string, z: string, deli: DelFiltra[]): JSX.Element =>
    deli.length === 0 ? (
      <span>{znesek(z)}</span>
    ) : (
      <button
        type="button"
        className="znesek"
        onClick={() => {
          odpri(naziv, naslov, deli);
        }}
      >
        {znesek(z)}
      </button>
    );
  if (telefon) {
    // Telefon (zasnova): vrstica na postavko – ime, rast, rast prihodkov in
    // delež v eni vrstici, desno znesek, ki odpre knjižbe.
    return (
      <section id={`kazalnik-${ns.koda}`} className="najvecja-stroska ns-telefon" aria-labelledby="naslov-najvecja-stroska">
        <h2 id="naslov-najvecja-stroska">{ns.naziv}</h2>
        {ns.postavke.map((p) => (
          <div key={p.kljuc} className="ns-vrstica">
            <div className="ns-vrstica-ime">
              <span>
                {p.mesto}. {p.naziv}
              </span>
              <span className="tiho">
                {[
                  p.rast === null ? null : `${sPredznakom(p.rast)} %`,
                  ns.prihodki.rast === null ? null : b.pk.nsPrihodkiRast(`${sPredznakom(ns.prihodki.rast)} %`),
                  p.delez === null ? null : b.pk.nsDelezKratko(decimalno(p.delez, 1)),
                ]
                  .filter((x) => x !== null)
                  .join(' · ')}
              </span>
            </div>
            <button
              type="button"
              className="ns-stevilka"
              onClick={() => {
                odpri(p.naziv, obdobje, p.deli);
              }}
            >
              {evri(p.znesek)}
            </button>
          </div>
        ))}
      </section>
    );
  }
  return (
    <section id={`kazalnik-${ns.koda}`} className="najvecja-stroska" aria-labelledby="naslov-najvecja-stroska">
      <div className="ns-glava">
        <h2 id="naslov-najvecja-stroska">{ns.naziv}</h2>
        <div className="ns-podnaslov">{b.pk.nsPodnaslov(obdobje, imaPrimerjalno ? ns.primerjalno : null)}</div>
      </div>
      <div className="ns-postavke">
        {ns.postavke.map((p) => (
          <div key={p.kljuc} className="ns-postavka">
            <div className="ns-vrh">
              <div className="ns-ime">
                <span className="ns-mesto">{p.mesto}.</span>
                <div>
                  <span className="ns-naziv">{p.naziv}</span>
                  <span className="ns-konti">
                    {p.delez === null ? p.konti : `${p.konti} · ${b.pk.nsDelez(decimalno(p.delez, 1))}`}
                  </span>
                </div>
              </div>
              <div className="ns-znesek">
                <button
                  type="button"
                  className="ns-stevilka"
                  title={b.pk.namigTocke(obdobje, ns.leto, znesekZValuto(p.znesek))}
                  onClick={() => {
                    odpri(p.naziv, obdobje, p.deli);
                  }}
                >
                  {evri(p.znesek)}
                </button>
                {p.zaPregled && (
                  <span className="oznaka-za-pregled">
                    <Lupa />
                    {b.pk.zaPregled}
                  </span>
                )}
              </div>
            </div>
            <div className="ns-trakovi">
              <span className="ns-trak-naziv">{b.pk.nsStrosek}</span>
              {p.rast === null ? <span /> : <TrakRasti rast={p.rast} barva="#256abf" />}
              <span className="ns-rast" title={p.razlogRasti ?? undefined}>
                {rastIzpis(p.rast)}
              </span>
              <span className="ns-trak-naziv tiho">{ns.prihodki.naziv}</span>
              {ns.prihodki.rast === null ? <span /> : <TrakRasti rast={ns.prihodki.rast} barva="#86b6ef" />}
              <span className="ns-rast tiho" title={ns.prihodki.razlog ?? undefined}>
                {rastIzpis(ns.prihodki.rast)}
              </span>
            </div>
          </div>
        ))}
      </div>
      {(ns.laniCeloLeto.length > 0 || ns.razlogLaniCeloLeto !== null) && (
        <div className="ns-lani">
          {ns.laniCeloLeto.length > 0
            ? b.pk.nsCeloLeto(
                ns.primerjalno,
                ns.laniCeloLeto
                  .map((p) => `${String(p.mesto)}. ${p.kratekNaziv} ${evri(p.znesek)}`)
                  .join(' · '),
              )
            : ns.razlogLaniCeloLeto}
        </div>
      )}
      {ns.opomba !== null && <div className="ns-opomba">{ns.opomba}</div>}
      <div className="ns-povezave">
        <button
          type="button"
          className="povezava-gumb"
          aria-expanded={pomen}
          onClick={() => {
            setPomen(!pomen);
          }}
        >
          {b.pk.kajPomeni}
        </button>
        <button type="button" className="povezava-gumb" aria-expanded={odprt} onClick={naOdpri}>
          {odprt ? b.pk.skrijIzracun : b.pk.pogledIzracun}
        </button>
      </div>
      {pomen && <p className="razlaga-kazalnika">{ns.opis}</p>}
      {odprt && (
        <div className="izracun-kazalnika ns-izracun">
          <p className="drobno">{ns.formula}</p>
          <table className="sestavine-izracuna">
            <thead>
              <tr>
                <th>{b.pk.nsPostavka}</th>
                <th className="stevilka">{obdobje}</th>
                <th className="stevilka">{imaPrimerjalno ? obdobjeLani : ''}</th>
                <th className="stevilka">{b.pk.nsRast}</th>
                <th className="stevilka">{b.pk.nsDelezStolpec}</th>
              </tr>
            </thead>
            <tbody>
              {[...ns.postavke.map((p) => ({ ...p, razlog: p.razlogRasti })), { ...ns.prihodki, kljuc: 'prihodki', delez: null, konti: '' }].map((v) => (
                <tr key={v.kljuc}>
                  <td className="postavka">
                    {v.naziv}
                    {v.konti !== '' && <span className="drobno ns-izracun-konti">{v.konti}</span>}
                  </td>
                  <td className="stevilka">
                    {znesekCelica(v.naziv, obdobje, v.znesek, v.deli)}
                  </td>
                  <td className="stevilka">
                    {v.lani === null ? (
                      <span title={v.razlog ?? undefined}>{b.pk.ni}</span>
                    ) : (
                      znesekCelica(v.naziv, obdobjeLani, v.lani, v.deliLani)
                    )}
                  </td>
                  <td className="stevilka" title={v.razlog ?? undefined}>
                    {rastIzpis(v.rast)}
                  </td>
                  <td className="stevilka">{v.delez === null ? '' : `${decimalno(v.delez, 1)} %`}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ns.laniCeloLeto.length > 0 && (
            <table className="sestavine-izracuna">
              <caption>{b.pk.nsCeloLeto(ns.primerjalno, '')}</caption>
              <tbody>
                {ns.laniCeloLeto.map((p) => (
                  <tr key={p.mesto}>
                    <td className="postavka">
                      {p.mesto}. {p.naziv}
                    </td>
                    <td className="stevilka">
                      {znesekCelica(p.naziv, String(ns.primerjalno), p.znesek, p.deli)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
